import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import PageContainer from '../../components/login/PageContainer';
import OnboardingHeader from '../../components/onboarding/OnboardingHeader';
import FooterLegalNavigation from '../../components/login/FooterLegalNavigation';
import useScreenSize from '../../utils/useScreenSize';
import useRouter from '../../utils/useRouter';
import { routes } from '../../constants';
import useCheckoutStatusPolling, { CheckoutPollState } from './useCheckoutStatusPolling';

// The onboarding flow (OnboardingPage, which reads the token as optInToken) lives at
// the top-level `/konto-erstellen` — the same target the onboarding email links to.
// NB: this is NOT `kontoErstellenPath` ('/registrierung/konto-erstellen'), which is
// the premium-registration PaymentPage.
const onboardingPath = `/${routes.onboardingPage.root}`;

interface StateCopy {
  title: string;
  subtitle: string;
  body?: string;
}

// Static, terminal "the email still has your link" copy — also shown when no
// session_id is present (nothing to poll) and on timeout.
const EMAIL_FALLBACK_BODY =
  'We have sent you a link to complete the creation of your account. Please follow the link in the email.';

// While polling we prepare the user's session; both in-progress states share the
// same "setting up" copy, and `ready` is the success state that unlocks the button.
const isLoadingState = (state: CheckoutPollState) =>
  state === 'confirmingPayment' || state === 'settingUpAccount';

const getStateCopy = (state: CheckoutPollState): StateCopy => {
  switch (state) {
    case 'confirmingPayment':
    case 'settingUpAccount':
      return { title: 'Setting up your account…', subtitle: 'Please wait a moment' };
    case 'ready':
      return { title: 'You\'re all set – let\'s go!', subtitle: 'Payment successful' };
    case 'expired':
      return {
        title: 'Your checkout session has expired',
        subtitle: 'Please start the checkout process again.',
      };
    case 'sessionNotFound':
      return {
        title: 'We couldn\'t find your checkout session',
        subtitle: 'Please check your email inbox',
        body: EMAIL_FALLBACK_BODY,
      };
    case 'checkoutStatusFailed':
      return {
        title: 'We couldn\'t confirm your payment',
        subtitle: 'Please check your email inbox',
        body: EMAIL_FALLBACK_BODY,
      };
    case 'emailFallback':
    default:
      return {
        title: 'Payment successful',
        subtitle: 'Please check your email inbox',
        body: EMAIL_FALLBACK_BODY,
      };
  }
};

const CheckoutSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const { router, match } = useRouter();
  const sessionId = match.location.query.session_id?.trim() || undefined;

  const { state, email, onboardingToken } = useCheckoutStatusPolling(sessionId);
  const { title, subtitle, body } = getStateCopy(state);

  const isLoading = isLoadingState(state);
  const isReady = state === 'ready';

  const handleCreateProfile = () => {
    if (!onboardingToken) return;
    router.replace(`${onboardingPath}?token=${encodeURIComponent(onboardingToken)}`);
  };

  return (
    <PageContainer pageBodySx={{ gap: 0 }}>
      <Box
        data-testid='checkout-success-page'
        data-state={state}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isXsScreen ? spaces.xl : spaces.xxxl,
        }}
      >
        <OnboardingHeader title={t(title)} subtitle={t(subtitle)} hideIcon />
        {isLoading && (
          <CircularProgress
            data-testid='checkout-success-spinner'
            sx={{ '& .MuiCircularProgress-svg': { width: 'auto', height: 'auto' } }}
          />
        )}
        {isReady && (
          <CheckCircleIcon
            data-testid='checkout-success-check'
            sx={{
              fontSize: 64,
              color: palette.success.main,
              animation: 'checkmark-pop 0.35s ease-out',
              '@keyframes checkmark-pop': {
                from: { transform: 'scale(0)', opacity: 0 },
                to: { transform: 'scale(1)', opacity: 1 },
              },
            }}
          />
        )}
        {body && (
          <Typography
            variant={isSmScreen ? 'body2' : 'body1'}
            sx={{
              color: palette.text.secondary,
              textAlign: 'center',
              marginTop: spaces.md,
            }}
          >
            {t(body)}
          </Typography>
        )}
        {email && (
          <Typography
            data-testid='checkout-success-email'
            variant={isSmScreen ? 'body2' : 'body1'}
            sx={{ color: palette.text.secondary, textAlign: 'center' }}
          >
            {t('Confirmation sent to:')}{' '}
            <Box component='span' sx={{ fontWeight: 'bold', color: palette.text.primary }}>
              {email}
            </Box>
          </Typography>
        )}
        {(isLoading || isReady) && (
          <Button
            data-testid='checkout-success-create-profile'
            variant='contained'
            disabled={!isReady}
            onClick={handleCreateProfile}
            fullWidth
          >
            {t('Create profile')}
          </Button>
        )}
      </Box>
      <FooterLegalNavigation isSmallScreen={isXsScreen} />
    </PageContainer>
  );
};

export default CheckoutSuccessPage;
